/**
 * Embla Carousel Fade plugin, patched for a "fade-out only" transition.
 *
 * Based on the v9 embla-carousel-fade source. Instead of crossfading, the
 * incoming slide stays fully opaque behind, and the outgoing slide fades out
 * on top of it. Every change from the original is marked with "CHANGED".
 */
const defaultOptions = {
	active: true,
	breakpoints: {}
};

function clampNumber(number, min, max) {
	return Math.min(Math.max(number, min), max);
}
function isNumber(value) {
	return typeof value === 'number' && !isNaN(value);
}

function Fade(userOptions = {}) {
	const fullOpacity = 1;
	const noOpacity = 0;
	const fadeFriction = 0.68;
	let options;
	let emblaApi;
	let isSsr = false;
	let destroyed = false;
	let opacities = [];
	let fadeToNextDistance;
	let distanceFromPointerDown = 0;
	let fadeVelocity = 0;
	let progress = 0;
	let shouldFadePair = false;
	let defaultSettledBehaviour;
	let defaultProgressBehaviour;

	function pluginIsActive() {
		if (isSsr) return false;
		if (destroyed) return false;
		return options.active;
	}

	function init(emblaApiInstance, optionsHandler) {
		emblaApi = emblaApiInstance;
		const {
			mergeOptions,
			optionsAtMedia
		} = optionsHandler;
		const optionsBase = mergeOptions(defaultOptions, Fade.globalOptions);
		const allOptions = mergeOptions(optionsBase, userOptions);
		destroyed = false;
		options = optionsAtMedia(allOptions);
		isSsr = emblaApi.internalEngine().isSsr;
		if (!pluginIsActive()) return;
		const selectedSnap = emblaApi.selectedSnap();
		const {
			scrollBody,
			containerRect,
			axis
		} = emblaApi.internalEngine();
		const containerSize = axis.getSize(containerRect);
		fadeToNextDistance = clampNumber(containerSize * 0.75, 200, 500);
		shouldFadePair = false;
		opacities = emblaApi.snapList().map((_, index) => index === selectedSnap ? fullOpacity : noOpacity);
		defaultSettledBehaviour = scrollBody.settled;
		defaultProgressBehaviour = emblaApi.scrollProgress;
		scrollBody.settled = fadeSettled;
		emblaApi.scrollProgress = fadeScrollProgress;
		emblaApi.on('select', select).on('slidefocus', fadeToSelectedSnapInstantly).on('pointerdown', pointerDown).on('pointerup', pointerUp);
		disableScroll();
		fadeToSelectedSnapInstantly();
	}

	function destroy() {
		if (!pluginIsActive()) return;
		const {
			scrollBody
		} = emblaApi.internalEngine();
		scrollBody.settled = defaultSettledBehaviour;
		emblaApi.scrollProgress = defaultProgressBehaviour;
		emblaApi.off('select', select).off('slidefocus', fadeToSelectedSnapInstantly).off('pointerdown', pointerDown).off('pointerup', pointerUp);
		emblaApi.slideNodes().forEach(slideNode => {
			const slideStyle = slideNode.style;
			slideStyle.opacity = '';
			slideStyle.pointerEvents = '';
			// CHANGED: also reset the stacking order set while fading.
			slideStyle.zIndex = '';
		});
		destroyed = true;
	}

	function fadeToSelectedSnapInstantly() {
		const selectedSnap = emblaApi.selectedSnap();
		setOpacities(selectedSnap, fullOpacity);
	}

	function fadeScrollProgress() {
		return progress;
	}

	function pointerUp() {
		shouldFadePair = false;
	}

	function pointerDown() {
		shouldFadePair = false;
		distanceFromPointerDown = 0;
		fadeVelocity = 0;
	}

	function select() {
		const duration = emblaApi.internalEngine().scrollBody.duration();
		fadeVelocity = duration ? 0 : fullOpacity;
		shouldFadePair = true;
		if (!duration) fadeToSelectedSnapInstantly();
	}

	function getSlideTransform(position) {
		const {
			axis
		} = emblaApi.internalEngine();
		const translateAxis = axis.scroll.toUpperCase();
		return `translate${translateAxis}(${axis.direction(position)}px)`;
	}

	function disableScroll() {
		const {
			translate,
			slideTranslates
		} = emblaApi.internalEngine();
		const translates = [translate, ...slideTranslates];
		translates.forEach(translate => {
			translate.clear();
			translate.toggleActive(false);
		});
		emblaApi.containerNode().style.transform = 'translate(0px,0px)';
	}

	function lockExcessiveScroll(fadeIndex) {
		const {
			scrollSnaps,
			location,
			target
		} = emblaApi.internalEngine();
		if (!isNumber(fadeIndex) || opacities[fadeIndex] < 0.5) return;
		location.set(scrollSnaps[fadeIndex]);
		target.set(location);
	}

	function setOpacities(fadeIndex, velocity) {
		const scrollSnaps = emblaApi.snapList();
		scrollSnaps.forEach((_, indexA) => {
			const absVelocity = Math.abs(velocity);
			const currentOpacity = opacities[indexA];
			const isFadeIndex = indexA === fadeIndex;
			const nextOpacity = isFadeIndex ? currentOpacity + absVelocity : currentOpacity - absVelocity;
			const clampedOpacity = clampNumber(nextOpacity, noOpacity, fullOpacity);
			opacities[indexA] = clampedOpacity;
			const fadePair = isFadeIndex && shouldFadePair;
			const indexB = emblaApi.previousSnap();
			if (fadePair) opacities[indexB] = 1 - clampedOpacity;
			if (isFadeIndex) setProgress(fadeIndex, clampedOpacity);
			// CHANGED: pass the incoming index so it can be rendered behind.
			setOpacity(indexA, fadeIndex);
		});
	}

	// CHANGED: the "opacities" array still drives the progress and settle logic,
	// but what gets painted is different. The incoming slide is fully opaque
	// behind (z-index 1), the outgoing slide fades out on top (z-index 2).
	function setOpacity(index, fadeIndex) {
		const {
			scrollSnaps,
			containerRect,
			scrollSnapList
		} = emblaApi.internalEngine();
		const slidesInSnap = scrollSnapList.slidesBySnap[index];
		const opacity = opacities[index];
		const isIncoming = index === fadeIndex;
		const visualOpacity = isIncoming ? (opacity > noOpacity ? fullOpacity : noOpacity) : opacity;
		slidesInSnap.forEach(slideIndex => {
			const slideStyle = emblaApi.slideNodes()[slideIndex].style;
			const roundedOpacity = parseFloat(visualOpacity.toFixed(2));
			const hasOpacity = roundedOpacity > noOpacity;
			const position = hasOpacity ? scrollSnaps[index] : containerRect.width + 2;
			const transform = getSlideTransform(position);
			if (hasOpacity) slideStyle.transform = transform;
			slideStyle.opacity = roundedOpacity.toString();
			slideStyle.zIndex = isIncoming ? '1' : '2';
			slideStyle.pointerEvents = opacity > 0.5 ? 'auto' : 'none';
			if (!hasOpacity) slideStyle.transform = transform;
		});
	}

	function setProgress(fadeIndex, opacity) {
		const {
			indexCurrent,
			dragHandler,
			scrollSnaps
		} = emblaApi.internalEngine();
		const pointerDown = dragHandler.pointerDown();
		const snapFraction = 1 / (scrollSnaps.length - 1);
		let indexA = fadeIndex;
		let indexB = pointerDown ? emblaApi.selectedSnap() : emblaApi.previousSnap();
		if (pointerDown && indexA === indexB) {
			const reverseSign = Math.sign(distanceFromPointerDown) * -1;
			indexA = indexB;
			indexB = indexCurrent.clone().set(indexB).add(reverseSign).get();
		}
		const currentPosition = indexB * snapFraction;
		const diffPosition = (indexA - indexB) * snapFraction;
		progress = currentPosition + diffPosition * opacity;
	}

	function getFadeIndex() {
		const {
			dragHandler,
			indexCurrent,
			scrollBody
		} = emblaApi.internalEngine();
		const selectedSnap = emblaApi.selectedSnap();
		if (!dragHandler.pointerDown()) return selectedSnap;
		const directionSign = Math.sign(scrollBody.velocity());
		const distanceSign = Math.sign(distanceFromPointerDown);
		const nextSnap = indexCurrent.clone().set(selectedSnap).add(directionSign * -1).get();
		if (!directionSign || !distanceSign) return null;
		return distanceSign === directionSign ? nextSnap : selectedSnap;
	}

	function fade(emblaApi) {
		const {
			dragHandler,
			scrollBody
		} = emblaApi.internalEngine();
		const pointerDown = dragHandler.pointerDown();
		const velocity = scrollBody.velocity();
		const duration = scrollBody.duration();
		const fadeIndex = getFadeIndex();
		const noFadeIndex = !isNumber(fadeIndex);
		if (pointerDown) {
			if (!velocity) return;
			distanceFromPointerDown += velocity;
			fadeVelocity = Math.abs(velocity / fadeToNextDistance);
			lockExcessiveScroll(fadeIndex);
		}
		if (!pointerDown) {
			if (!duration || noFadeIndex) return;
			fadeVelocity += (fullOpacity - opacities[fadeIndex]) / duration;
			fadeVelocity *= fadeFriction;
		}
		if (noFadeIndex) return;
		setOpacities(fadeIndex, fadeVelocity);
	}

	function fadeSettled() {
		const {
			target,
			location
		} = emblaApi.internalEngine();
		const displacement = target.minus(location);
		const notReachedTarget = Math.abs(displacement) >= 1;
		const fadeIndex = getFadeIndex();
		const noFadeIndex = !isNumber(fadeIndex);
		fade(emblaApi);
		if (noFadeIndex || notReachedTarget) return false;
		return opacities[fadeIndex] > 0.999;
	}

	function createFadeSsrStyles(options, containerSelector, slidesSelector) {
		const {
			active
		} = options;
		const {
			slidesBySnap
		} = emblaApi.internalEngine().scrollSnapList;
		const selectedSnap = emblaApi.selectedSnap();
		const opacity = active ? 0 : 1;
		const pointerEvents = active ? 'none' : 'auto';
		const baseStyles = `${containerSelector} ${slidesSelector}{opacity:${opacity};pointer-events:${pointerEvents};}`;
		const slideStyles = slidesBySnap[selectedSnap].reduce((styles, index) => {
			return styles + `${containerSelector} ${slidesSelector}:nth-child(${index + 1}){opacity:1;pointer-events:auto;}`;
		}, '');
		if (active) return baseStyles + slideStyles;
		return baseStyles;
	}

	function ssrStyles(containerSelector, slidesSelector = '> *') {
		if (!isSsr) return '';
		const optionBreakpoints = userOptions.breakpoints || {};
		const baseStyles = createFadeSsrStyles(options, containerSelector, slidesSelector);
		const mediaStyles = Object.keys(optionBreakpoints).reduce((styles, key) => {
			return styles + `@media ${key}{${createFadeSsrStyles(optionBreakpoints[key], containerSelector, slidesSelector)}}`;
		}, '');
		return baseStyles + mediaStyles;
	}

	const self = {
		name: 'fade',
		options: userOptions,
		init,
		destroy,
		ssrStyles
	};
	return self;
}
Fade.globalOptions = undefined;

export { Fade as default };
